// apps/web/src/features/admin/form-config/recognition/AccuracyDashboard.tsx
import { ConfusedPairsTable } from './ConfusedPairsTable';
import { ConfidenceHistogram } from './ConfidenceHistogram';
import { CorrectionsStream } from './CorrectionsStream';
import { LearnedRulesPanel } from './LearnedRulesPanel';
// R-64a: own the stylesheet here so consumers (IntelligenceTab, RecognitionTab)
// don't need to remember to import it.
import './recognition.css';

interface AccuracyDashboardProps {
  total: number;
  corrected: number;
  recent: Array<{ timestamp: string; documentId: string; wrongFormId: string; correctFormId: string }>;
  /** R-64b: only render the Avg confidence metric when the API supplies it. Until
   *  the cron aggregator + accuracy endpoint produce it, IntelligenceTab passes
   *  no value and the metric is omitted (vs. showing "0.00" forever). */
  averageConfidence?: number;
  /** R-64b: same — render the table only when the API supplies real pairs. */
  topConfused?: Array<{ wrongFormId: string; correctFormId: string; count: number }>;
  /** R-64b: same — render the histogram only when the API supplies real buckets. */
  confidenceBuckets?: Array<{ min: number; max: number; count: number }>;
  jobTypeId?: string;
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="metric">
      <div className="metric__num">{value}</div>
      <div className="metric__lbl">{label}</div>
    </div>
  );
}

export function AccuracyDashboard({
  total,
  corrected,
  averageConfidence,
  topConfused,
  recent,
  confidenceBuckets,
  jobTypeId,
}: AccuracyDashboardProps) {
  const accuracy = total ? `${((1 - corrected / total) * 100).toFixed(1)}%` : '\u2014';

  return (
    <div className="accuracy-dashboard" data-testid="accuracy-dashboard" data-job-type-id={jobTypeId}>
      {/* WU-9.4 (R-64c) — operator guidance for the "should I retrain?" question.
          The dashboard metrics on their own don't answer it; small sample sizes
          produce noisy numbers and a single vendor's confused-pair list can
          mislead. This blurb gives concrete thresholds. */}
      <details
        className="accuracy-dashboard__retrain-note"
        data-testid="accuracy-dashboard-retrain-note"
      >
        <summary>When should I retrain?</summary>
        <ul>
          <li>
            <strong>Corrections rising:</strong> retrain when corrected /
            classifications &gt; 10% over a 7-day window.
          </li>
          <li>
            <strong>Vendor-specific drop:</strong> retrain when a new vendor's
            accuracy falls below 90% across 20+ samples.
          </li>
          <li>
            <strong>Sample size matters:</strong> the bigger the sample, the
            stabler the metric. Wait for 50+ classifications before acting on a
            single vendor's confused-pair list — one or two bad weeks aren't a
            trend.
          </li>
        </ul>
      </details>
      <div className="accuracy-dashboard__metrics-row">
        <Metric label="7-day accuracy" value={accuracy} />
        <Metric label="Classifications" value={total} />
        <Metric label="Operator corrections" value={corrected} />
        {averageConfidence !== undefined && (
          <Metric label="Avg confidence" value={averageConfidence.toFixed(2)} />
        )}
      </div>
      {topConfused && topConfused.length > 0 && <ConfusedPairsTable pairs={topConfused} />}
      {confidenceBuckets && confidenceBuckets.length > 0 && (
        <ConfidenceHistogram buckets={confidenceBuckets} />
      )}
      <CorrectionsStream events={recent} />
      {/* WU-5.3: learned rules are tenant-wide — no job-type scoping exists upstream */}
      <LearnedRulesPanel />
    </div>
  );
}
